<script type="ts">
  import FaSort from "svelte-icons/fa/FaSort.svelte";
  import FaSortDown from "svelte-icons/fa/FaSortDown.svelte";
  import FaSortUp from "svelte-icons/fa/FaSortUp.svelte";
  import { tooltip } from "svooltip";
  import 'svooltip/styles.css';
  import type { SeriesOptions} from "../data-views/types";
  import { t, locale, findKeyByValue } from "../translations/i18n";
  import translations from "../translations/translation";

  type TableDataMatrix = (number | string | null)[][];
  type TableVisualisationData = {
    data: TableDataMatrix;
    metadata: SeriesOptions[];
  };

  export let tableData: TableVisualisationData;
  export let sortedAscending: boolean;
  export let sortedBy: string;
  let isDaily: boolean = false;

  let currentLanguage: any = $locale.split("-")[0]

  /**
   * Sort the column by the column name
   * @param e 
   */
  const sortColumn = (e: Event): void => {
    const element = e.target as HTMLElement;
    const columnName = element.textContent ? element.textContent.trim() : '';
    sortedAscending = columnName === sortedBy ? !sortedAscending : true;
    sortedBy = columnName;
    tableData.data = sortRowsByColumn(tableData.data, columnName, sortedAscending);
    isDaily = tableData.metadata[0].is_daily;
  };

  /**
   * Sort the rows by the column
   * @param tableData 
   * @param column 
   * @param ascending 
   * @returns 
   */
  const sortRowsByColumn = (
    tableData: TableDataMatrix,
    column: string,
    ascending: boolean = true
  ) => {
    const columnIndex = tableData[0].indexOf(column);
    const sortedTableData = tableData.slice(1).sort((a, b) => {
      const valueA = parseValue(a[columnIndex]);
      const valueB = parseValue(b[columnIndex]);

      if (valueA === valueB) return 0;

      // If the value is numeric, it should be sorted as number1 Tammi, 2023 	
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return ascending ? valueA - valueB : valueB - valueA;
      }

      // If the value is null, it should be sorted to the bottom
      if (valueA === null || valueA === "") return 1;
      
      // If the value is stirng, it should change to date and then sort
      const dateA = parseMonthYear(valueA);
      const dateB = parseMonthYear(valueB);

      // If the value is date, it should be sorted as date
      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return 1;
      if (dateB === null) return -1;

      if (dateA < dateB) return ascending ? -1 : 1;
      if (dateA > dateB) return ascending ? 1 : -1;
      return 0;
    });

    return [tableData[0], ...sortedTableData];
  };
  
  /**
   * Parse the month year string to date 
  */
  function parseValue(value: any) {
    if (isNumeric(value)) {
      // Remove commas and parse as a float
      return parseFloat(value.replace(/,/g, ''));
    }
    return value;
  }

  /**
   * Parse the month year string to date 
  */
  function parseMonthYear(value: any) {
    if (value === null) return null;
    if (isNumeric(value)) return null;
    const monthList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let standardizedDate;

    const monthYearString = String(value).replace(",", "");

    // Check if the date is daily date, pass the day with month and year, else pass the month and year
    if (isDaily){
      const [day , month, year] = monthYearString.split(' ').slice(-3);
      standardizedDate = new Date(Number(year), monthList.indexOf(findKeyByValue(translations[$locale], month)), Number(day));
    } else {
      const [month, year] = monthYearString.split(' ').slice(-2);
      
      if (!month || !year) return null;
      standardizedDate = new Date(Number(year), monthList.indexOf(findKeyByValue(translations[$locale], month)));
    }
    if (isNaN(standardizedDate.getTime())) return null;

    return standardizedDate;
  };

  /**
   * Check if the input is a valid daily date string.
   * @param input 
   * @returns 
   */
  function isValidDailyDate(value: any) {
    const datePattern = /^(\d{1,2})\s+([A-Za-z]+),\s+(\d{4})$/;
    return datePattern.test(value);
  }

  /**
   * Check if the input is a number
   * @param value 
   * @returns 
   */
  function isNumeric(value: any) {

    if (value === null) {
      return false;
    }

    const sanitizedValue = String(value).replace(/,/g, '');
    
    // If the value is a DAILY date, it should be treated as a number
    if (isValidDailyDate(value)) {
      return !isNaN(Number(sanitizedValue));
    } else {
      return !isNaN(parseFloat(sanitizedValue));
    }
  };

  /**
   * Create tooltip content for the table
   * @param index 
   * @returns 
   */
  function createTooltipContent(index: number) {

    let optionsDescription = ``;

    // Iterate through the options and create option description
    Object.entries(tableData.metadata[index].verbose_options).forEach(([key, value]) => {
      optionsDescription += `<p>${key.charAt(0).toUpperCase() + key.slice(1)}: ${value[currentLanguage]}</p>`;
    });

    let tooptip_content = `<h3>${tableData.metadata[index].statVerboseName[currentLanguage]}</h3>${optionsDescription}`;

    return tooptip_content;
  }
</script>

<table data-testid="table-test">
  <thead>
    <tr>
      {#each tableData.data[0] as column}
        <th on:click={sortColumn}>
          <div class="column-label">
            <span>
              {column}
            </span>
            <div class="icon">
              {#if column == sortedBy}
                {#if sortedAscending}
                  <FaSortUp/>
                {:else}
                  <FaSortDown />
                {/if}
              {:else}
                <FaSort />
              {/if}
            </div>
          </div>
        </th>
      {/each}
    </tr>
  </thead>
  <tbody>
    {#each tableData.data.slice(1) as row}
      <tr>
        <th>{row[0]}</th>
        {#each row.slice(1) as value , i}
          <td
          use:tooltip={{
            content:  createTooltipContent(i),
            placement: 'top',
            delay: [250, 0],
            offset: 15,
            html: true,
            target: 'div.icon',
          }}
          >{value !== null ? value : ""}</td>
        {/each}
      </tr>
    {/each}
  </tbody>
</table>

<style lang="scss">
  table {
    width: 100%;
    border-collapse: collapse;
    text-align: center;
    color: var(--color-gray-dark);

    thead {
      position: sticky;
      top: 0;
      z-index: 1;

      tr th {
        cursor: pointer;
        user-select: none;
        color: var(--color-white);
        background-color: var(--color-primary);

        &:first-child {
          border-radius: 0.5rem 0 0 0;
          position: sticky;
          left: 0;
          top: 0;
        }

        &:last-child {
          border-radius: 0 0.5rem 0 0;
        }

        &:not(:first-child) {
          min-width: 165px;
        }
      }

      .column-label {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;

        .icon {
          pointer-events: none;
        }
      }
    }

    tbody tr {
      border-bottom: 0.5px solid #dddddd;
      background-color: var(--color-white);

      th:first-child {
        position: sticky;
        left: 0;
        text-align: center;
        background-color: var(--color-white);
        background-clip: padding-box;
      }

      &:nth-of-type(even) th:first-child {
        background-color: #f3f3f3;
      }

      &:nth-of-type(even) {
        background-color: #f3f3f3;
      }

      &:last-of-type {
        border-bottom: 2px solid var(--color-primary);
      }

      &:hover {
        font-weight: bold;
        color: var(--color-primary);
      }
  }

  th,
  td {
    padding: 0.5rem 1rem;
  }
}
</style>
